import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { Settings } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOpenUrl } from "../../hooks/useOpenUrl";
import { CallScriptSelector } from "../Script/CallScriptSelector";

export interface ScriptSelectionModalProps {
  isOpen: boolean;
  onSelect: (script: CallScript) => void;
  onCancel?: () => void;
}

export function ScriptSelectionModal({
  isOpen,
  onSelect,
  onCancel,
}: Readonly<ScriptSelectionModalProps>) {
  const [selectedScript, setSelectedScript] = useState<CallScript | null>(null);
  const openUrl = useOpenUrl();

  const handleSelect = () => {
    if (selectedScript) {
      onSelect(selectedScript);
      setSelectedScript(null);
    }
  };

  const handleCancel = () => {
    setSelectedScript(null);
    onCancel?.();
  };

  const handleManageScripts = async () => {
    const dashUrl =
      import.meta.env.VITE_DASH_URL || "https://dash.frontierxai.com";
    const scriptsUrl = `${dashUrl}/scripts`;
    await openUrl(scriptsUrl);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select a script for this call</DialogTitle>
          <DialogDescription>
            Choose a script to guide you through this call. You can change it
            later if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">
                Available scripts
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleManageScripts}
                className="h-6 px-2 text-xs text-gray-600 hover:text-gray-900"
                title="Manage scripts in dashboard"
              >
                <Settings className="h-3 w-3 mr-1" />
                Manage
              </Button>
            </div>
            <CallScriptSelector
              selectedScript={selectedScript}
              onSelect={setSelectedScript}
              label="Choose script"
              size="default"
            />
          </div>

          <div className="flex justify-end space-x-2">
            {onCancel && (
              <Button variant="outline" onClick={handleCancel}>
                Skip for now
              </Button>
            )}
            <Button
              onClick={handleSelect}
              disabled={!selectedScript}
              className="min-w-[100px]"
            >
              Start call
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
